/**
 * useManifestGroups.js
 * ====================
 * Takes a flat manifest array and returns tools grouped by category
 * in display order: Workspace → Comms → Media → System → Memory.
 * Any unexpected categories are appended at the end.
 *
 * Usage
 * -----
 *   const { groups, toolCount } = useManifestGroups(manifest);
 *   // groups: [{ category: "Workspace", tools: [...] }, ...]
 *   // toolCount: total number of registered tools
 */

import { useMemo } from 'react';

const CATEGORY_ORDER = ['Workspace', 'Comms', 'Media', 'System', 'Memory'];

export function useManifestGroups(manifest) {
  return useMemo(() => {
    if (!Array.isArray(manifest) || manifest.length === 0) {
      return { groups: [], toolCount: 0 };
    }

    // Bucket tools by category
    const byCategory = {};
    for (const tool of manifest) {
      const cat = tool.category || 'Other';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(tool);
    }

    // Build groups in canonical order, skipping empty categories
    const groups = CATEGORY_ORDER
      .filter(cat => byCategory[cat]?.length > 0)
      .map(cat => ({ category: cat, tools: byCategory[cat] }));

    // Append any categories not in CATEGORY_ORDER (future-proofing)
    for (const [cat, tools] of Object.entries(byCategory)) {
      if (!CATEGORY_ORDER.includes(cat)) {
        groups.push({ category: cat, tools });
      }
    }

    return { groups, toolCount: manifest.length };
  }, [manifest]);
}
